import { FC, useEffect, useState } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Select, Switch } from 'antd'
import { translate } from '../../../core'
import {
  SHIPPING_METHOD_KIND_LABEL,
  ShippingMethod,
  ShippingMethodInput,
  ShippingMethodKind,
} from '../type/shipping-method'

interface Props {
  open: boolean
  editing: ShippingMethod | null
  saving: boolean
  onCancel: () => void
  onSubmit: (dto: ShippingMethodInput) => void
}

const KIND_OPTIONS = Object.values(ShippingMethodKind).map(kind => ({
  value: kind,
  label: SHIPPING_METHOD_KIND_LABEL[kind],
}))

export const ShippingMethodModal: FC<Props> = ({ open, editing, saving, onCancel, onSubmit }) => {
  const [form] = Form.useForm<ShippingMethodInput>()
  const [kind, setKind] = useState<ShippingMethodKind>(ShippingMethodKind.Delivery)

  useEffect(() => {
    if (!open) return
    const values: ShippingMethodInput = editing
      ? {
        key: editing.key,
        title: editing.title,
        description: editing.description ?? '',
        checkoutNoticeTitle: editing.checkoutNoticeTitle ?? '',
        checkoutNotice: editing.checkoutNotice ?? '',
        kind: editing.kind,
        basePrice: editing.basePrice,
        isActive: editing.isActive,
      }
      : {
        key: '', title: '', description: '', checkoutNoticeTitle: '', checkoutNotice: '',
        kind: ShippingMethodKind.Delivery, basePrice: 0, isActive: true,
      }

    form.setFieldsValue(values)
    setKind(values.kind ?? ShippingMethodKind.Delivery)
  }, [open, editing, form])

  const isFree = (value?: ShippingMethodKind) =>
    value === ShippingMethodKind.StorePickup || value === ShippingMethodKind.Fallback
  const priceDisabled = isFree(kind)
  const isFallback = kind === ShippingMethodKind.Fallback

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      onSubmit({
        ...values,
        key: values.key.trim().toUpperCase(),
        description: values.description?.trim() || null,
        checkoutNoticeTitle: values.checkoutNoticeTitle?.trim() || null,
        checkoutNotice: values.checkoutNotice?.trim() || null,
        basePrice: isFree(values.kind) ? 0 : values.basePrice ?? 0,
      })
    } catch {
      // no-op: field errors are already rendered
    }
  }

  return (
    <Modal
      open={open}
      title={editing ? translate('edit', 'shipping_method') : translate('add', 'shipping_method')}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      destroyOnClose
    >
      {editing?.isSystem && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={translate('shipping_method_system_hint')}
        />
      )}

      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="key"
          label={translate('key')}
          tooltip={translate('shipping_method_key_tooltip')}
          rules={[
            { required: true, message: translate('missing_field_message', 'key') },
            {
              pattern: /^[A-Za-z0-9-]{2,64}$/,
              message: translate('shipping_method_key_format'),
            },
          ]}
        >
          <Input
            autoFocus={!editing}
            disabled={!!editing?.isSystem}
            placeholder="UPS-GROUND"
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>

        <Form.Item
          name="title"
          label={translate('title')}
          rules={[{ required: true, message: translate('missing_field_message', 'title') }]}
        >
          <Input autoFocus={!!editing} />
        </Form.Item>

        <Form.Item name="description" label={translate('description')}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>

        <Form.Item
          name="checkoutNoticeTitle"
          label={translate('checkout_notice_title')}
          tooltip={translate('checkout_notice_tooltip')}
        >
          <Input maxLength={255} placeholder={translate('checkout_notice_title_placeholder')} />
        </Form.Item>

        <Form.Item
          name="checkoutNotice"
          label={translate('checkout_notice')}
          tooltip={translate('checkout_notice_tooltip')}
        >
          <Input.TextArea rows={4} maxLength={1000} showCount />
        </Form.Item>

        <Form.Item name="kind" label={translate('shipping_method_kind')}>
          <Select
            options={KIND_OPTIONS}
            onChange={setKind}
            disabled={!!editing?.isSystem}
          />
        </Form.Item>

        <Form.Item
          name="basePrice"
          label={translate('base_price')}
          extra={priceDisabled
            ? translate(isFallback ? 'fallback_is_always_free' : 'store_pickup_is_free')
            : undefined}
        >
          <InputNumber min={0} precision={2} prefix="$" disabled={priceDisabled} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="isActive" label={translate('status')} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
